import React from 'react';

import { StatusChip } from '@/components/ui/status-chip';
import { ReconObservation } from '@/sections/asset/hooks/drawer/useLLMRecon';

/**
 * Dynamic renderer for augustus reconnaissance observations.
 *
 * augustus emits every recon result as a generic envelope
 * (pkg/output.Observation): a `type` slug, a `target`, a producing `source`, and
 * a type-specific `data` payload. Rather than hard-code a panel per type, this
 * renders each observation structurally off its `data`: objects become
 * label/value rows, arrays of objects become tables, nested values recurse. A
 * new recon module therefore appears in the LLM Recon tab with no UI change.
 *
 * Types that warrant a bespoke view (e.g. mcp.inventory's tool/resource/prompt
 * cards) are rendered by LLMReconSection and passed to `exclude` here, so they
 * aren't also rendered generically. Everything else falls through to the generic
 * structural renderer below.
 */

const ACRONYMS = new Set([
  'mcp',
  'llm',
  'ssrf',
  'bola',
  'api',
  'id',
  'ids',
  'url',
  'uri',
  'oob',
  'cwe',
]);

/** "mcp.identifiers" -> "MCP Identifiers"; "server_name" -> "Server Name". */
function humanize(raw: string): string {
  return raw
    .split(/[.\-_\s]+/)
    .filter(Boolean)
    .map(w =>
      ACRONYMS.has(w.toLowerCase())
        ? w.toUpperCase()
        : w.charAt(0).toUpperCase() + w.slice(1)
    )
    .join(' ');
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function isEmpty(v: unknown): boolean {
  return (
    v === null ||
    v === undefined ||
    v === '' ||
    (Array.isArray(v) && v.length === 0) ||
    (isPlainObject(v) && Object.keys(v).length === 0)
  );
}

/** Compact, single-line rendering for a table cell or inline value. */
function Cell({ value }: { value: unknown }): React.ReactElement {
  if (isEmpty(value))
    return <span className="text-chariot-text-tertiary">—</span>;
  if (typeof value === 'boolean') {
    return (
      <StatusChip
        variant={value ? 'info' : 'muted'}
        label={value ? 'true' : 'false'}
      />
    );
  }
  if (isPlainObject(value) || Array.isArray(value)) {
    return (
      <code className="text-chariot-text-secondary font-mono text-xs break-all">
        {JSON.stringify(value)}
      </code>
    );
  }
  return <span className="break-all">{String(value)}</span>;
}

/** Column order = first-seen union of keys across all row objects. */
function unionKeys(rows: Record<string, unknown>[]): string[] {
  const keys: string[] = [];
  const seen = new Set<string>();
  for (const row of rows) {
    for (const k of Object.keys(row)) {
      if (!seen.has(k)) {
        seen.add(k);
        keys.push(k);
      }
    }
  }
  return keys;
}

function Table({ rows }: { rows: Record<string, unknown>[] }) {
  const cols = unionKeys(rows);
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead className="text-chariot-text-tertiary text-xs uppercase">
          <tr>
            {cols.map(c => (
              <th key={c} className="py-1 pr-4 font-medium">
                {humanize(c)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-chariot-border border-t align-top">
              {cols.map(c => (
                <td
                  key={c}
                  className="text-chariot-text-primary py-1 pr-4 font-mono"
                >
                  <Cell value={row[c]} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Structural renderer: objects -> rows, arrays-of-objects -> tables, recurse. */
function StructuredValue({
  value,
  depth = 0,
}: {
  value: unknown;
  depth?: number;
}): React.ReactElement {
  if (isEmpty(value)) {
    return <div className="text-chariot-text-tertiary text-sm">None</div>;
  }

  // Recon data comes from the (untrusted) target MCP server, which could return
  // deeply nested payloads. Cap recursion so an adversarial server can't stack-
  // overflow / crash the analyst's tab; render the rest as a scalar/JSON leaf.
  if (depth > 8) {
    return <Cell value={value} />;
  }

  if (Array.isArray(value)) {
    if (value.every(isPlainObject)) {
      return <Table rows={value as Record<string, unknown>[]} />;
    }
    return (
      <div className="flex flex-wrap gap-1">
        {value.map((v, i) => (
          <span
            key={i}
            className="bg-chariot-layer1 text-chariot-text-secondary rounded px-2 py-0.5 font-mono text-xs"
          >
            {typeof v === 'object' && v !== null
              ? JSON.stringify(v)
              : String(v)}
          </span>
        ))}
      </div>
    );
  }

  if (isPlainObject(value)) {
    return (
      <div className="space-y-1">
        {Object.entries(value).map(([k, v]) => {
          // Nested object/array: label above its own structured block.
          if (isPlainObject(v) || (Array.isArray(v) && v.some(isPlainObject))) {
            return (
              <div key={k} className="py-1">
                <div className="text-chariot-text-secondary mb-1 text-sm">
                  {humanize(k)}
                </div>
                <div className="border-chariot-border border-l pl-3">
                  <StructuredValue value={v} depth={depth + 1} />
                </div>
              </div>
            );
          }
          return (
            <div
              key={k}
              className="flex items-start justify-between gap-4 py-1"
            >
              <span className="text-chariot-text-secondary text-sm">
                {humanize(k)}
              </span>
              <span className="text-chariot-text-primary text-right text-sm font-medium">
                <Cell value={v} />
              </span>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="text-chariot-text-primary text-sm">
      <Cell value={value} />
    </div>
  );
}

function ObservationCard({ observation }: { observation: ReconObservation }) {
  return (
    <div className="bg-chariot-layer0 border-chariot-border rounded-lg border">
      <div className="border-chariot-border flex items-center justify-between gap-2 border-b px-4 py-3">
        <h4 className="text-chariot-text-primary text-sm font-medium">
          {humanize(observation.type)}
          {observation.target ? (
            <span className="text-chariot-text-tertiary ml-2 font-normal">
              {observation.target}
            </span>
          ) : null}
        </h4>
        {observation.source ? (
          <StatusChip variant="default" label={observation.source} />
        ) : null}
      </div>
      <div className="p-4">
        <StructuredValue value={observation.data} />
      </div>
    </div>
  );
}

/**
 * ReconObservations renders every observation generically. Callers may exclude
 * types that already have a dedicated panel (e.g. mcp.inventory) via `exclude`.
 */
export function ReconObservations({
  observations,
  exclude = [],
}: {
  observations: ReconObservation[];
  exclude?: string[];
}) {
  const shown = observations.filter(o => !exclude.includes(o.type));
  if (shown.length === 0) return null;
  return (
    <>
      {shown.map((o, i) => (
        <ObservationCard
          key={`${o.type}-${o.target ?? ''}-${i}`}
          observation={o}
        />
      ))}
    </>
  );
}
